import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSoloStore from '../stores/soloStore';
import { soloWordDatabaseService } from '../Services/soloWordDatabaseService';
import { generateBotAnswers } from '../Services/soloBotPlayer';
import SoloPlayPhase from './SoloPlayPhase';
import SoloReviewPhase from './SoloReviewPhase';
import SoloRoundResults from './SoloRoundResults';
import SoloFinalResults from './SoloFinalResults';

const ALL_LETTERS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

function pickNextLetter(playedLetters) {
  const remaining = ALL_LETTERS.filter((l) => !playedLetters.includes(l));
  if (remaining.length === 0) return ALL_LETTERS[Math.floor(Math.random() * 26)];
  return remaining[Math.floor(Math.random() * remaining.length)];
}

export default function SoloGame() {
  const navigate = useNavigate();
  const {
    gamePhase,
    selectedBot,
    categories,
    currentLetter,
    playedLetters,
    currentRound,
    setCurrentLetter,
    setBotAnswers,
    resetGame,
  } = useSoloStore();

  const [dbLoading, setDbLoading] = useState(!soloWordDatabaseService.initialized);
  const [dbError, setDbError] = useState(false);

  // Initialize word database once on mount
  useEffect(() => {
    if (soloWordDatabaseService.initialized) return;
    soloWordDatabaseService.initialize().then((ok) => {
      setDbLoading(false);
      if (!ok) setDbError(true);
    });
  }, []);

  // When playing phase starts, ensure letter is set and bot answers are generated
  useEffect(() => {
    if (gamePhase !== 'playing' || dbLoading) return;

    if (!currentLetter) {
      // Fresh round — pick a new letter
      const letter = pickNextLetter(playedLetters);
      setCurrentLetter(letter);
      useSoloStore.setState((s) => ({
        playedLetters: s.playedLetters.includes(letter)
          ? s.playedLetters
          : [...s.playedLetters, letter],
      }));
      setBotAnswers({});
      generateBotAnswers(letter, categories, selectedBot).then(setBotAnswers);
    } else {
      // Letter already set (e.g. page refresh) — just regenerate bot answers
      setBotAnswers({});
      generateBotAnswers(currentLetter, categories, selectedBot).then(setBotAnswers);
    }
  }, [gamePhase, currentLetter, dbLoading, currentRound]);

  // Guard: if no game is active, redirect to setup
  useEffect(() => {
    if (gamePhase === 'idle') {
      navigate('/solo', { replace: true });
    }
  }, [gamePhase]);

  if (dbLoading) {
    return (
      <div className="min-h-screen bg-orange-50 flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" />
        <p className="text-gray-500 font-medium">Loading game data...</p>
      </div>
    );
  }

  if (dbError) {
    // Not a fatal error — game continues with bot using blank answers
    console.warn('[SoloGame] Word database unavailable. Bot will use blank answers.');
  }

  switch (gamePhase) {
    case 'playing':
      return currentLetter ? <SoloPlayPhase /> : (
        <div className="min-h-screen bg-orange-50 flex items-center justify-center">
          <div className="w-10 h-10 border-4 border-red-200 border-t-red-600 rounded-full animate-spin" />
        </div>
      );
    case 'reviewing':
      return <SoloReviewPhase />;
    case 'round_results':
      return <SoloRoundResults />;
    case 'finished':
      return <SoloFinalResults />;
    default:
      return null;
  }
}
